import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { sendMessage } from '../../redux/chat.redux';
import type { Message } from '../../types/message';
import { avatarText } from '../../utils/format';

interface Props {
  open: boolean;
  message: Message | null;
  onClose: () => void;
}

export default function ForwardDialog({ open, message, onClose }: Props) {
  const dispatch = useAppDispatch();
  const conversations = useAppSelector((s) => s.chat.conversations);
  const activeId = useAppSelector((s) => s.chat.activeConversationId);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState('');

  React.useEffect(() => {
    if (open) {
      setDone(null);
      setErr('');
    }
  }, [open]);

  if (!message) return null;

  const targets = conversations.filter((c) => c.id !== activeId);

  const forward = async (cid: number, name: string) => {
    try {
      await dispatch(sendMessage({ cid, content: message.content })).unwrap();
      setDone(name);
    } catch (e: any) {
      setErr(e?.message || '转发失败');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>转发消息</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1.5 }}>
          <Typography variant="body2" noWrap>
            {message.content}
          </Typography>
        </Box>
        {done ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            已转发到「{done}」
          </Alert>
        ) : (
          <>
            {err && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {err}
              </Alert>
            )}
            {targets.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                没有可转发的会话
              </Typography>
            ) : (
              <List disablePadding sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {targets.map((c) => (
                  <ListItemButton key={c.id} onClick={() => forward(c.id, c.display_name)}>
                    <ListItemAvatar>
                      <Avatar src={c.display_avatar || undefined}>{avatarText({ nickname: c.display_name, username: c.display_name })}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={c.display_name}
                      secondary={c.type === 'group' ? '群聊' : '私聊'}
                      secondaryTypographyProps={{ fontSize: 12 }}
                    />
                    <SendIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
}
